import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { IAuth } from '../../store/Auth'
import { IGeneral } from '../../store/General'
import { DeniedCard } from "../ui/state-views";

export type IPermissionCode =
  | "ORD_DASH"
  | "ORD_LIST"
  | "ORD_SO_DETAIL"
  | "ORD_SPLIT"
  | "ORD_SPLIT_DN_ASSIGN"
  | "ORD_SO_DRSHIP"
  | "ORD_SEND"
  | "ORD_SAVE"
  | "ORD_EDIT"
  | "ORD_CANCEL"
  | "ORD_SALES_LIST"
  | "ORD_DN_ASSIGN"
  | "ORD_PO_LIST"
  | "ORD_PO_ACCEPT"
  | "ORD_BCK_ORD"
  | "ORD_PO_DECLINE"
  | "ORD_DRSHPING"
  | "ORD_DRSHPING_DN_ASSIGN"
  | "ORD_DRSHPING_DETAIL"
  | "ORD_DROPPED"
  | "ERP_PGH_MARGIN_VIEW"
  | "ORD_DN_DRAFT_LIST"
  | "ORD_DN_ASSIGNED_LIST"
  | "ORD_DN_DETAIL"
  | "ORD_DN_PULLSHEET_ASSIGN"
  | "ORD_DN_DELIVERED_LIST"
  // Manager-tier delivery-note management (POD verify/reject, metrics, config).
  // Real catalog code (PERMISSIONS.ORDER.ORD_DN_MNG) enforced by the BE.
  | "ORD_DN_MNG"
  | "ORD_DN_PDF"
  | "ORD_PS_LIST"
  | "ORD_PS_DETAIL"
  | "ORD_PS_PDF"
  | "ORD_PS_MNG"
  | "ORD_NET_LIST"
  | "ORD_NET_DETAIL"
  | "ORD_NET_NEW_ORDER"
  // Received quotations (de-price-service routes/api/received-quotation/*), portal BOTH.
  | "PRICING_QUOTATION_LIST"
  | "PRICING_QUOTATION_GET"
  | "PRICING_QUOTATION_ACCEPT"
  | "PRICING_QUOTATION_REJECT"
  // Invoices sent to the buyer (de-invoice-service GET /invoice/received), buyer dashboard.
  | "INV_LIST"
  | "ORD_DN_CREATE"
  | "DEL_ROUTE_PLAN"
  // Delivery management (BE delivery-note/failed-*, cod-reconciliation/settle, governance-config).
  | "DEL_CHANNEL_OVERRIDE"
  | "LOG_CLASS_RULE_MNG"
  | "LOG_SIZE_MNG"
  | "BIZ_GOV_VIEW"
  | "BIZ_GOV_MNG"
  | "ORD_VMI_REVERT"
  // mvp-returns §D3 — off-system buyer claim CS verification
  | "RETURN_OFFSYSTEM_VERIFY"
  // mvp-returns §D13 — in-transit exception report + resolve
  | "RETURN_REPORT_TRANSIT_EXCEPTION"
  | "RETURN_WAREHOUSE_RECEIVE"
  | "RETURN_RESOLVE_TRANSIT"
  // mvp-returns §18 / §9 — manager escalation + dispute override
  | "RETURN_ESCALATE"
  // mvp-returns Phase 2 — buyer returns surface (screens-buyer-app). Real
  // catalog codes (sequelize-models ORDER perms). RETURN_VIEW gates the buyer
  // list/detail; the rest gate the buyer manager/owner mutations (D19).
  | "RETURN_VIEW"
  | "RETURN_CREATE"
  | "RETURN_ACCEPT"
  | "RETURN_DISPUTE"
  | "RETURN_CANCEL"
  | "RETURN_MARK_SHIPPED"
  // Seller-side return actions (BE routes/api/return/*: propose, reject, set-resolution,
  // confirm-refund, schedule-pickup, inventory-impact + damaged-inventory/bulk-dispose,
  // failed-ops and comment overrides). Real catalog codes checked by assertReturnPermission.
  | "RETURN_PROPOSE"
  | "RETURN_REJECT"
  | "RETURN_SET_RESOLUTION"
  | "RETURN_CONFIRM_REFUND"
  | "RETURN_SCHEDULE_PICKUP"
  | "RETURN_SET_INVENTORY_IMPACT"
  | "DAMAGED_INVENTORY_VIEW"
  | "RETURN_FAILED_OP_MNG"
  | "RETURN_MANAGER_OVERRIDE"
  // Dashboard returns widget (BE return-prevention-alert/*, analytics).
  | "RETURN_ANALYTICS_VIEW"
  // Dropship create (BE order/dropshipping/*).
  | "ORD_DRSHP"
  // Inbound orders (BE inbound-order/*, contract §1 «Орлого»).
  | "IBN_CREATE"
  | "IBN_APPROVE"
  | "IBN_DISPATCH"
  | "IBN_ASSIGN"
  | "IBN_RECEIVE"
  | "IBN_CANCEL";

export function usePermission(code: IPermissionCode) {
  const { t } = useTranslation("common");
  const user = useSelector((state: { auth: IAuth }) => state.auth.user);
  const permissions = useSelector((state: { general: IGeneral }) => state.general.permissions);

  /** Тайван саарал карт «Эрх хэрэгтэй: КОД» (улаан Alert биш, DeHUB UI стандарт). */
  function accessDenied() {
    return <DeniedCard permission={code} />;
  }

  const text = permissions?.find((item: any) => item.code === code)?.name ?? t("permission.accessDenied");
  const hasAccess = user?.permissions?.some((som: any) => som?.code === code) || false;

  /** Эрхгүй үед товчны tooltip-д харуулах шалтгаан; эрхтэй бол null. */
  const reason = hasAccess ? null : t("permission.required", { code });

  return {
    text,
    hasAccess,
    reason,
    accessDenied,
  };
}
